import { useLingui, Trans } from '@lingui/react/macro';
import { Card, CardContent, Button, Input, Label } from '@pantry/ui';
import { type StorageLocation } from '@pantry/api-client';
import { type InventorySortBy, type SortOrder } from '@pantry/shared';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  QuestionMarkCircleIcon,
  Squares2X2Icon,
  ListBulletIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
} from '@heroicons/react/24/outline';

export function InventoryFilterBar({
  searchInput,
  onSearchChange,
  locationFilter,
  onLocationChange,
  storageLocations,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  isFetching,
  isStartingBasicsTour,
  onStartBasicsTour,
}: {
  searchInput: string;
  onSearchChange: (value: string) => void;
  locationFilter: string;
  onLocationChange: (value: string) => void;
  storageLocations: StorageLocation[] | undefined;
  viewMode: 'grouped' | 'flat';
  onViewModeChange: (mode: 'grouped' | 'flat') => void;
  sortBy: InventorySortBy;
  onSortByChange: (value: InventorySortBy) => void;
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
  isFetching: boolean;
  isStartingBasicsTour: boolean;
  onStartBasicsTour: () => void;
}) {
  const { t } = useLingui();

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] relative" data-tour="inventory-search">
            <Label htmlFor="search" className="sr-only">
              <Trans>Search items</Trans>
            </Label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                type="text"
                placeholder={t`Search items...`}
                className="pl-10 bg-muted/50 border-0 focus-visible:ring-1"
                value={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {isFetching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              )}
            </div>
          </div>

          {/* Location filter */}
          <div className="flex items-center gap-2" data-tour="inventory-location-filter">
            <FunnelIcon className="h-4 w-4 text-muted-foreground" />
            <select
              id="location"
              aria-label={t`Filter by storage location`}
              className="h-10 rounded-lg border-0 bg-muted/50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-w-[160px]"
              value={locationFilter}
              onChange={(e) => onLocationChange(e.target.value)}
            >
              <option value="">{t({ message: 'All locations', context: 'filter_option' })}</option>
              {storageLocations?.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center gap-1 border rounded-lg p-0.5">
            <Button
              variant={viewMode === 'grouped' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onViewModeChange('grouped')}
              aria-label={t`Grouped view`}
              aria-pressed={viewMode === 'grouped'}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'flat' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onViewModeChange('flat')}
              aria-label={t`Flat list view`}
              aria-pressed={viewMode === 'flat'}
            >
              <ListBulletIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Sort by */}
          <select
            className="h-10 rounded-lg border-0 bg-muted/50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value as InventorySortBy)}
            aria-label={t`Sort items by`}
          >
            <option value="created_at">
              {t({ message: 'Date added', context: 'sort_option' })}
            </option>
            <option value="expiry_date">
              {t({ message: 'Expiry date', context: 'sort_option' })}
            </option>
            <option value="name">{t({ message: 'Name', context: 'sort_option' })}</option>
          </select>

          {/* Sort order toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-10 w-10 p-0"
            onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
            aria-label={sortOrder === 'asc' ? t`Sort descending` : t`Sort ascending`}
          >
            {sortOrder === 'asc' ? (
              <BarsArrowUpIcon className="h-4 w-4" />
            ) : (
              <BarsArrowDownIcon className="h-4 w-4" />
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto"
            onClick={onStartBasicsTour}
            data-tour="inventory-help-basics"
            aria-label={t`Start inventory basics mini tour`}
            title={t`Inventory basics help`}
            aria-busy={isStartingBasicsTour}
            disabled={isStartingBasicsTour}
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
